import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, PlayCircle,
  Loader2, Info, ChevronLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { AD_PROVIDER, AD_ZONE, AD_SDK_URL } from "@/lib/h5ads";

/**
 * AdsDebug — live Monetag diagnostic page.
 *
 * Replaces the vague "Ad unavailable" toast with a step-by-step readout of
 * exactly which link in the ad-serving chain is broken:
 *
 *   1. Did the Monetag SDK loader snippet execute?
 *   2. Is the vignette.min.js script actually loaded? (ad-blocker test)
 *   3. Is window.show_<zone>() defined? (SDK fully initialised)
 *   4. Does a REAL show_<zone>() call succeed right now?
 *
 * Mounted at two routes:
 *   /admin/ads-debug   — admin-gated (via /api/admin/me)
 *   /debug/ads         — hidden/unlisted URL, no auth gate (nothing sensitive)
 */

const SHOW_FN_NAME = `show_${AD_ZONE}`;

/** Status pill — green/yellow/red based on severity. */
function Row({ label, status, value, hint }) {
  const Icon = status === "ok" ? CheckCircle2 : status === "warn" ? AlertTriangle : XCircle;
  const color =
    status === "ok" ? "#5ac77a" : status === "warn" ? "#d4af37" : "#8a0303";
  return (
    <div
      className="flex items-start gap-3 py-3 border-b"
      style={{ borderColor: "rgba(240,230,210,0.08)" }}
      data-testid={`ads-debug-row-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <Icon size={16} style={{ color, marginTop: 2, flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] tracking-[0.3em] ash-text">{label}</div>
        <div
          className="font-mono text-[13px] mt-1 break-all"
          style={{ color: status === "fail" ? "#ff9999" : "#f0e6d2" }}
        >
          {value}
        </div>
        {hint && (
          <div className="text-[11px] italic mt-1" style={{ color: "#b8a788" }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdsDebug({ requireAdmin = false }) {
  const [checks, setChecks] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [running, setRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [auth, setAuth] = useState(requireAdmin ? null : { is_admin: true });

  useEffect(() => {
    if (!requireAdmin) return;
    (async () => {
      try {
        await api.get("/admin/me");
        setAuth({ is_admin: true });
      } catch (e) {
        if (e?.response?.status === 401) setAuth({ is_admin: false, error: "not_signed_in" });
        else setAuth({ is_admin: false, error: "not_admin" });
      }
    })();
  }, [requireAdmin]);

  const runChecks = useCallback(async () => {
    setRunning(true);
    const results = {};

    // 1) Monetag SDK script loaded → tag.min.js in DOM
    const scriptTags = Array.from(document.querySelectorAll("script[src*='tag.min.js']"));
    results.sdk_script_in_dom = scriptTags.length > 0;
    results.sdk_script_src = scriptTags[0]?.src || "(not in DOM)";

    // 2) The global show_<zone> function is present (SDK fully initialised)
    results.show_fn_present = typeof window[SHOW_FN_NAME] === "function";

    // 3) Network reachability — can we hit the Monetag CDN? (direct blocker probe)
    try {
      await fetch(`${AD_SDK_URL}?t=${Date.now()}`, { mode: "no-cors", cache: "no-store" });
      results.sdk_reachable = true;
    } catch (e) {
      results.sdk_reachable = false;
      results.sdk_reachable_error = String(e).slice(0, 120);
    }

    // 4) Domain / origin info
    results.origin = window.location.origin;
    results.hostname = window.location.hostname;
    results.href = window.location.href;
    results.protocol = window.location.protocol;
    results.referrer = document.referrer || "(none)";

    setChecks(results);

    // ---- Compute overall verdict ----
    // tag.min.js is Monetag's multi-format AUTO-SERVE zone — it displays ads
    // on its own schedule (vignette on nav, in-page push, etc.) and does NOT
    // register a show_<zone>() function. That is NORMAL for this zone type,
    // not a failure. We only mark the integration as failed if the CDN is
    // unreachable or the script tag isn't in the DOM.
    let v = "ok";
    if (!results.sdk_reachable) v = "fail";              // ad-blocker
    else if (!results.sdk_script_in_dom) v = "fail";     // script not in DOM
    else if (results.protocol !== "https:") v = "warn";
    // show_fn missing is INFORMATIONAL — surfaced in the row + help panel,
    // not treated as a verdict-level failure.
    setVerdict(v);
    setRunning(false);
  }, []);

  useEffect(() => { runChecks(); }, [runChecks]);

  // Fire a real Monetag show_<zone>() call and capture the outcome.
  const runLiveTest = async () => {
    if (testing) return;
    setTesting(true);
    setTestResult(null);
    const trace = [];
    const push = (ev, data = {}) => trace.push({ t: Date.now(), ev, data });

    if (typeof window[SHOW_FN_NAME] !== "function") {
      push("abort", { reason: `window.${SHOW_FN_NAME} is not a function — Monetag SDK not loaded` });
      setTestResult({ outcome: "fail", trace });
      setTesting(false);
      return;
    }

    push("show_fn_called", { zone: AD_ZONE });
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      push("timeout", { ms: 20000 });
      setTestResult({ outcome: "timeout", trace });
      setTesting(false);
    }, 20000);

    try {
      window[SHOW_FN_NAME]()
        .then(() => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          push("resolved", { note: "ad watched to completion → reward would be granted" });
          setTestResult({ outcome: "ok", trace });
          setTesting(false);
        })
        .catch((err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          push("rejected", { error: String(err).slice(0, 200) });
          setTestResult({ outcome: "fail", trace });
          setTesting(false);
        });
    } catch (e) {
      clearTimeout(timeout);
      settled = true;
      push("exception", { error: String(e) });
      setTestResult({ outcome: "fail", trace });
      setTesting(false);
    }
  };

  // ---- Gating UI ----
  if (requireAdmin && auth === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Weighing the ember...</div>
      </div>
    );
  }
  if (requireAdmin && !auth.is_admin) {
    const goSignIn = () => {
      const redirectUrl = window.location.origin + "/admin/ads-debug";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="panel p-10 max-w-lg text-center" data-testid="ads-debug-gate">
          <div className="text-[10px] tracking-[0.4em] blood-text">RESTRICTED</div>
          <h1 className="font-heading text-4xl mt-3 text-[#f0e6d2] italic">Ad Diagnostics</h1>
          <p className="ash-text mt-3 text-sm italic">
            {auth.error === "not_signed_in"
              ? "Sign in with an authorized administrator account."
              : "Your account does not have keeper's access."}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            {auth.error === "not_signed_in" && (
              <button onClick={goSignIn} className="btn-primary" data-testid="ads-debug-signin-btn">
                SIGN IN
              </button>
            )}
            <Link to="/" className="btn-glow" data-testid="ads-debug-back-btn">
              RETURN TO REALM
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Main page ----
  return (
    <div className="min-h-screen px-4 md:px-8 py-6" data-testid="ads-debug-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="ash-text hover:text-white flex items-center gap-1 text-xs tracking-widest"
              data-testid="ads-debug-nav-dashboard"
            >
              <ChevronLeft size={14} /> DASHBOARD
            </Link>
            <span className="ash-text">·</span>
            <h1 className="font-heading text-2xl md:text-3xl italic text-[#f0e6d2]">
              Ad <span className="ember">Diagnostics</span>
            </h1>
            <span
              className="ml-2 text-[10px] tracking-widest px-2 py-1 rounded font-mono"
              style={{ background: "rgba(226,88,34,0.15)", color: "#ffb458", border: "1px solid rgba(226,88,34,0.4)" }}
              data-testid="ads-debug-provider-badge"
            >
              {AD_PROVIDER.toUpperCase()} · zone {AD_ZONE}
            </span>
          </div>
          <button
            onClick={runChecks}
            disabled={running}
            className="btn-glow flex items-center gap-2 text-xs tracking-widest"
            data-testid="ads-debug-rerun"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            RE-RUN
          </button>
        </div>

        {/* Verdict banner */}
        {verdict && checks && (
          <div
            className="panel p-5 mb-5"
            data-testid="ads-debug-verdict"
            style={{
              borderColor:
                verdict === "ok" ? "#5ac77a" :
                verdict === "warn" ? "#d4af37" : "#8a0303",
              background:
                verdict === "ok" ? "rgba(90,199,122,0.08)" :
                verdict === "warn" ? "rgba(212,175,55,0.08)" :
                "rgba(138,3,3,0.12)",
            }}
          >
            <div className="flex items-start gap-3">
              {verdict === "ok" ? <CheckCircle2 size={22} style={{ color: "#5ac77a" }} /> :
                verdict === "warn" ? <AlertTriangle size={22} style={{ color: "#d4af37" }} /> :
                <XCircle size={22} style={{ color: "#8a0303" }} />}
              <div className="flex-1">
                <div className="font-heading text-xl text-[#f0e6d2]">
                  {verdict === "ok" && "Monetag SDK loaded · ads will serve"}
                  {verdict === "warn" && "Monetag SDK loaded with warning"}
                  {verdict === "fail" && "Critical issue — ads cannot serve"}
                </div>
                <div className="text-sm ash-text mt-2 leading-relaxed">
                  {verdict === "fail" && !checks.sdk_reachable && (
                    <>
                      <strong style={{ color: "#ff9999" }}>Ad-blocker is active on this browser.</strong>{" "}
                      <code>quge5.com</code> (Monetag's CDN) was blocked at the network layer.
                      Disable uBlock / AdGuard / Brave Shields on this site and reload.
                    </>
                  )}
                  {verdict === "fail" && checks.sdk_reachable && !checks.sdk_script_in_dom && (
                    <>
                      The Monetag <code>&lt;script&gt;</code> tag is missing from <code>/public/index.html</code>.
                      Hard-reload (Cmd/Ctrl+Shift+R) and check the browser console for errors.
                    </>
                  )}
                  {verdict === "warn" && checks.protocol !== "https:" && (
                    <>Monetag requires HTTPS in production. You are on <code>{checks.protocol}</code>.</>
                  )}
                  {verdict === "ok" && (
                    <>
                      Zone <code>{AD_ZONE}</code> is a <strong>multi-format auto-serve zone</strong>.
                      Monetag will show ads automatically during gameplay — real monetization is active.
                      {!checks.show_fn_present && (
                        <>
                          {" "}The <strong>"Watch Ad to Revive"</strong> button cannot be triggered manually
                          by this zone type and will fall through to the <strong>free revive fallback (2/day)</strong>
                          — players stay unstuck either way. To gate revives behind an ad, create a separate
                          <strong> "Rewarded Interstitial"</strong> zone in your Monetag dashboard (its snippet
                          will have a <code>data-sdk="show_XXXXX"</code> attribute).
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Static checks */}
        <div className="panel p-5 mb-5" data-testid="ads-debug-checks">
          <div className="text-[10px] tracking-[0.4em] gold-text mb-3">RUNTIME ENVIRONMENT</div>
          {!checks ? (
            <div className="flex items-center gap-2 ash-text text-sm">
              <Loader2 size={14} className="animate-spin" /> Running checks…
            </div>
          ) : (
            <>
              <Row
                label="Monetag CDN reachable"
                status={checks.sdk_reachable ? "ok" : "fail"}
                value={checks.sdk_reachable ? "quge5.com responded" : "BLOCKED"}
                hint={!checks.sdk_reachable &&
                  "An ad-blocker, DNS filter, or corporate firewall is intercepting Monetag's CDN. Disable it on this site and reload."}
              />
              <Row
                label="tag.min.js in DOM"
                status={checks.sdk_script_in_dom ? "ok" : "fail"}
                value={checks.sdk_script_in_dom ? checks.sdk_script_src : "NOT FOUND"}
                hint={!checks.sdk_script_in_dom && "Check /public/index.html for the Monetag <script> tag."}
              />
              <Row
                label={`window.${SHOW_FN_NAME} (manual trigger)`}
                status={checks.show_fn_present ? "ok" : "warn"}
                value={checks.show_fn_present ? "function available" : "not registered — zone is auto-serve"}
                hint={!checks.show_fn_present &&
                  "This is NORMAL for multi-format zones (tag.min.js). Ads serve automatically — no manual trigger needed. The revive button will use the free-revive fallback (2/day). To gate revives behind a real ad, create a separate 'Rewarded Interstitial' zone in Monetag."}
              />
              <Row
                label="Protocol"
                status={checks.protocol === "https:" ? "ok" : "warn"}
                value={checks.protocol}
                hint={checks.protocol !== "https:" && "Monetag ads only serve reliably over HTTPS in production."}
              />
              <Row
                label="Origin"
                status="ok"
                value={checks.origin}
                hint={
                  <>
                    Monetag does not require pre-registering specific domains — any site using your
                    zone ID will serve. If you have whitelist/blacklist rules in your dashboard,
                    confirm this origin is allowed.
                  </>
                }
              />
            </>
          )}
        </div>

        {/* Live ad test */}
        <div className="panel p-5 mb-5" data-testid="ads-debug-live">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.4em] gold-text">LIVE AD TEST</div>
            <button
              onClick={runLiveTest}
              disabled={testing || !checks?.show_fn_present}
              className="btn-primary flex items-center gap-2 text-xs"
              data-testid="ads-debug-live-test-btn"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
              {testing ? "WAITING ON MONETAG…" : "TRIGGER TEST REWARDED AD"}
            </button>
          </div>
          <p className="text-xs ash-text italic">
            Calls <code>window.{SHOW_FN_NAME}()</code> and captures whether the rewarded promise
            resolves (ad watched → reward would fire) or rejects (user closed early / no fill / network).
          </p>

          {!checks?.show_fn_present && (
            <div
              className="mt-3 p-3 rounded flex items-start gap-2 text-xs ash-text"
              style={{ background: "rgba(138,3,3,0.1)", border: "1px solid rgba(138,3,3,0.4)" }}
            >
              <AlertTriangle size={14} className="blood-text mt-[2px] flex-shrink-0" />
              Live test disabled — the Monetag SDK (<code>window.{SHOW_FN_NAME}</code>) isn't ready.
              Fix the issues above first.
            </div>
          )}

          {testResult && (
            <div
              className="mt-4 p-4 rounded"
              data-testid="ads-debug-live-result"
              style={{
                background: "rgba(10,5,2,0.6)",
                border: `1px solid ${testResult.outcome === "ok" ? "#5ac77a" : "#8a0303"}`,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                {testResult.outcome === "ok" ? (
                  <CheckCircle2 size={16} style={{ color: "#5ac77a" }} />
                ) : (
                  <XCircle size={16} style={{ color: "#8a0303" }} />
                )}
                <span className="font-heading text-lg text-[#f0e6d2]">
                  {testResult.outcome === "ok" ? "AD SERVED SUCCESSFULLY" :
                   testResult.outcome === "timeout" ? "TIMED OUT (20s)" :
                   "AD DID NOT COMPLETE"}
                </span>
              </div>

              <div className="text-[10px] tracking-[0.3em] ash-text mb-2">CALLBACK TRACE</div>
              <pre
                className="font-mono text-[11px] p-3 rounded overflow-x-auto whitespace-pre-wrap"
                style={{ background: "rgba(0,0,0,0.5)", color: "#b8a788", maxHeight: 320 }}
              >
                {JSON.stringify(testResult.trace, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <div className="panel p-5" data-testid="ads-debug-help">
          <div className="text-[10px] tracking-[0.4em] gold-text mb-3">
            IF LIVE TEST FAILS
          </div>
          <ol className="text-sm ash-text space-y-2 list-decimal list-inside leading-relaxed">
            <li>
              <strong style={{ color: "#f0e6d2" }}>Check zone status</strong> — in your
              Monetag dashboard, confirm zone <code>{AD_ZONE}</code> is <em>Active</em>
              (not Pending Review, Paused, or Rejected).
            </li>
            <li>
              <strong style={{ color: "#f0e6d2" }}>Try an incognito window</strong> with
              extensions disabled and VPN off. Monetag fill varies by region and user profile.
            </li>
            <li>
              <strong style={{ color: "#f0e6d2" }}>Add your Monetag ads.txt</strong> — copy
              the ads.txt entries from your Monetag dashboard into <code>/public/ads.txt</code>.
              This improves fill rate materially.
            </li>
            <li>
              <strong style={{ color: "#f0e6d2" }}>Low initial fill is normal</strong> — new
              zones take 24–48h to warm up. The free-revive fallback (2/day) handles this.
            </li>
          </ol>
        </div>

        <div className="mt-6 text-center text-[10px] tracking-[0.3em] ash-text opacity-60">
          <Info size={10} className="inline mr-1" />
          Diagnostic snapshot generated client-side · no sensitive data shown
        </div>
      </div>
    </div>
  );
}
